import React from 'react';
import { useTranslation } from 'react-i18next';

import { Typography, getTypographyClassaname } from '@components/typography';

import { cn } from '@/lib/utils';

import IntegrationsVideo from '@assets/integrationsVideo.mp4';
import ScaleImg from '@assets/scale.avif';
import SellAsleepImg from '@assets/sellAsleep.avif';

export function FeaturesSectionMain() {
  const { t } = useTranslation();

  const features = [
    {
      title: t('featurecard1title'),
      description: t('featurecard1description'),
      skeleton: <SkeletonOne />,
      className:
        'border border-l-[1px]! border-b-0 lg:border-r-0 col-span-1 lg:col-span-2 dark:border-neutral-800 rounded-ss-lg rounded-se-lg lg:rounded-ss-lg lg:rounded-se-none',
    },
    {
      title: t('featurecard2title'),
      description: t('featurecard2description'),
      skeleton: <SkeletonTwo />,
      className: 'col-span-1 lg:col-span-4 border border-b-0 dark:border-neutral-800 lg:rounded-se-lg',
    },
    {
      title: t('featurecard3title'),
      description: t('featurecard3description'),
      skeleton: <SkeletonThree />,
      className: 'col-span-1 lg:col-span-4 border-b border dark:border-neutral-800 lg:rounded-es-lg',
    },
    {
      title: t('featurecard4title'),
      description: t('featurecard4description'),
      skeleton: <SkeletonFour />,
      className:
        'border border-l-1 border-t-0 lg:border-t-1 lg:border-l-0 col-span-1 lg:col-span-2 dark:border-neutral-800 rounded-es-lg rounded-ee-lg lg:rounded-ee-lg lg:rounded-es-none',
    },
  ];

  return (
    <div className="relative z-20 pt-[10vh] lg:pt-[20vh] max-w-7xl mx-auto">
      <div className="px-0 lg:px-5 pb-0 lg:pb-[5vh] w-full flex flex-col items-center">
        <h4
          className={cn(
            'cursor-default px-0 lg:px-0 lg:leading-tight w-full mx-auto text-center tracking-tight font-medium text-black dark:text-white flex justify-center items-start',
            getTypographyClassaname('header', 'center')
          )}
        >
          {t('Packed with thousands of features')}
        </h4>
        <p className={cn('w-full lg:w-[650px] my-4 px-0', getTypographyClassaname('base', 'center'))}>
          {t('Everything your business needs to generate more money and customers')}
        </p>
      </div>

      <div className="relative">
        <div className="border-0! grid grid-cols-1 lg:grid-cols-6 mt-12 xl:border rounded-md dark:border-neutral-800">
          {features.map((feature, i) => (
            <FeatureCard
              key={`featuremain-${i}`}
              className={cn(
                feature.className,
                getTypographyClassaname('header', 'start'),
                i === 0 ? 'pb-0! flex flex-col' : '',
                'bg-[#fbfbfb] flex flex-col justify-between'
              )}
            >
              <div className="flex flex-col p-4 lg:p-8 pb-0!">
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </div>
              <div className="h-fit w-full">{feature.skeleton}</div>
            </FeatureCard>
          ))}
        </div>
      </div>
    </div>
  );
}

const FeatureCard = ({ children, className }: { children?: React.ReactNode; className?: string }) => {
  return <div className={cn(`p-0 relative overflow-hidden`, className)}>{children}</div>;
};

const FeatureTitle = ({ children }: { children?: React.ReactNode }) => {
  return (
    <Typography
      variant="base"
      align="start"
      className="flex justify-start! items-start! w-full font-semibold! text-black!"
    >
      {children}
    </Typography>
  );
};

const FeatureDescription = ({ children }: { children?: React.ReactNode }) => {
  return (
    <p className={cn('w-full font-normal my-2', getTypographyClassaname('base', 'start'))}>{children}</p>
  );
};

export const SkeletonOne = () => {
  return (
    <div className="w-full flex flex-col">
      <div className="border-gray-100 w-full lg:grow flex flex-col items-center justify-center rounded-[12px] overflow-hidden p-2">
        <div
          className="rounded-[8px] h-[500px]"
          style={{
            aspectRatio: 604 / 1080,
            objectPosition: 'center center',
          }}
        >
          <div className="wistia_responsive_padding w-full h-full" style={{ position: 'relative' }}>
            <div
              className="wistia_responsive_wrapper"
              style={{
                height: '100%',
                left: 0,
                position: 'absolute',
                top: 0,
                width: '100%',
                padding: '0px',
              }}
            >
              <iframe
                src="https://fast.wistia.net/embed/iframe/gzp5m4ywwj?web_component=true&seo=true"
                title="segundo_sc Video"
                allow="autoplay; fullscreen"
                className="wistia_embed"
                name="wistia_embed"
                width="100%"
                height="100%"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SkeletonTwo = () => {
  return (
    <div className="flex flex-col items-center relative bg-transparent dark:bg-transparent mt-0">
      <div className="flex flex-col">
        <div className="w-full p-0 border-0">
          <img className="w-full px-12" src={ScaleImg}></img>
        </div>
      </div>
    </div>
  );
};

export const SkeletonThree = () => {
  return (
    <div className="flex flex-col items-center relative bg-transparent dark:bg-transparent mt-0">
      <div className="flex flex-col p-4">
        <div className="w-full border-1 rounded-md p-2 bg-white">
          <img className="w-full rounded-md" src={SellAsleepImg}></img>
        </div>
      </div>
    </div>
  );
};

export const SkeletonFour = () => {
  return (
    <div className="flex flex-col items-center relative bg-transparent dark:bg-transparent mt-0">
      <div className="flex flex-col">
        <div className="w-full p-0 border-0">
          <video
            src={IntegrationsVideo}
            muted
            loop
            autoPlay
            playsInline
            style={{
              aspectRatio: 719 / 478,
              objectPosition: 'center center',
            }}
          ></video>
        </div>
      </div>
    </div>
  );
};
